import { FC, ReactNode, useRef } from "react";
import { useConfig, useConfigContext } from "@dit/ui-configurations/configurations";
import { GlobalConfigDefinition } from "@app/core/globalConfig";
import { LocalesHelper } from "@app/core/i18n/localesHelper";
import i18n from "i18next";
import Backend from "i18next-http-backend";
import LanguageDetector from "i18next-browser-languagedetector";
import intervalPlural from "i18next-intervalplural-postprocessor";
import { initReactI18next } from "react-i18next";
import * as configuration from "@app/core/configuration";
import { languageDetectorPlugin } from "@app/core/i18n/languageDetector";
import { isNullOrWhitespace } from "@app/core/utilities/string";

const getUri = (apiUrl: string) => {
    return `${apiUrl}/translations/portal/{{ns}}/{{lng}}`;
};

export const I18nConfiguration: FC<{ children?: ReactNode }> = ({ children }) => {
    const config = useConfig<I18nDefinition, GlobalConfigDefinition>(c => c.i18n);
    const globalConfig = useConfigContext<GlobalConfigDefinition>();
    const hasInitializedRef = useRef(false);

    // initialize i18n synchronously during render (not in useEffect) to avoid empty flash
    if (config && !hasInitializedRef.current) {
        const { languages, fallbackLng: fallbackLngConfig } = config;
        const supportedLngs = languages.map(lng => LocalesHelper.normalizeLocale(lng));
        const normalizeFallbackLng =
            !isNullOrWhitespace(fallbackLngConfig) && LocalesHelper.normalizeLocale(fallbackLngConfig);
        const fallbackLng = normalizeFallbackLng || supportedLngs[0];

        const languageDetector = new LanguageDetector();
        languageDetector.addDetector(languageDetectorPlugin({ fallbackLng, supportedLngs }));

        i18n.use(Backend)
            .use(languageDetector)
            .use(intervalPlural)
            .use(initReactI18next)
            .init({
                detection: {
                    order: ["languageDetectorPlugin"],
                    lookupQuerystring: "lang",
                    caches: ["localStorage"],
                },
                backend: {
                    loadPath: getUri(globalConfig.services.LocalizationAPI.uri),
                    withCredentials: true,
                    crossDomain: true,
                    customHeaders: { ["X-Tenant-Name"]: globalConfig.tenantKey },
                },
                fallbackLng,
                debug: configuration.IsDevelopment,
                ns: ["common"],
                fallbackNS: "common",
                interpolation: {
                    escapeValue: false,
                },
                returnObjects: true,
            });
        hasInitializedRef.current = true;
    }

    if (!config) {
        return null;
    }

    return <>{children}</>;
};
